import React, { useMemo } from 'react'
import { Box, Typography, Chip } from '@mui/material'
import { useAppSelector } from '../../../../../store'
import { selectEater } from '../../../../../store/profileSlice'
import { debugLog } from '../../../../../utils/log-helper'

interface HowToEarnPointModelProps {
  points?: string[] // Deprecated: kept for backward compatibility, will be ignored if eater data available
}

/**
 * Static marketing list for unauthenticated users
 * Matches backend PointsReason values
 */
const STATIC_EARN_WAYS = [
  { title: 'Sign up', points: 100 },
  { title: 'Follow Instagram', points: 50 },
  { title: 'Scan a new menu', points: 500 },
  { title: 'Scan an existing menu', points: 10 },
  { title: 'Upload a receipt', points: 200 },
  { title: 'Leave a review', points: 10 },
  { title: 'Share a restaurant', points: 25 },
] as const

const HowToEarnPoints: React.FC<HowToEarnPointModelProps> = ({ points: legacyPoints }) => {
  const eater = useAppSelector(selectEater)
  
  // Get earn ways from backend, sorted by sortOrder
  const sortedEarnWays = useMemo(() => {
    if (!eater?.earnWays || !Array.isArray(eater.earnWays) || eater.earnWays.length === 0) {
      return []
    }
    
    // Include all earn ways (make_reservation is now shown with "Coming soon" label)
    // Sort by sortOrder ascending
    return [...eater.earnWays].sort((a, b) => {
      if (a.sortOrder !== undefined && b.sortOrder !== undefined) {
        return a.sortOrder - b.sortOrder
      }
      if (a.sortOrder !== undefined) return -1
      if (b.sortOrder !== undefined) return 1
      return 0
    })
  }, [eater?.earnWays])
  
  // Check if upload_receipt earnWay exists
  const hasUploadReceipt = useMemo(() => {
    return sortedEarnWays.some((way) => way.key === 'upload_receipt')
  }, [sortedEarnWays])
  
  // Get earn way status map
  const earnWayStatus = eater?.earnWayStatus ?? {}
  
  // If eater is null/unauthenticated, show static marketing list
  if (!eater && !legacyPoints) {
    return (
      <Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          {STATIC_EARN_WAYS.map((item, index) => (
            <Box
              key={index}
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 2,
              }}
            >
              <Typography variant="subtitle1" sx={{ flex: 1 }}>
                {item.title}
              </Typography>
              <Chip
                label={`+${item.points} pts`}
                size="small"
                sx={{
                  backgroundColor: 'transparent',
                  borderColor: 'grey.300',
                  color: 'primary.light',
                  border: 1,
                  fontWeight: 400,
                  fontSize: '12px',
                  height: '28px',
                }}
              />
            </Box>
          ))}
        </Box>
        
        {/* Disclaimer for unauthenticated users */}
        <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid', borderColor: 'grey.200' }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            Points unlock rewards. Credits are subject to availability.
          </Typography>
        </Box>
      </Box>
    )
  }
  
  // If eater exists but no earnWays available, show friendly fallback
  if (eater && sortedEarnWays.length === 0 && !legacyPoints) {
    return (
      <Box>
        <Typography variant="body2" color="text.secondary">
          Earn ways are loading. Please try again.
        </Typography>
      </Box>
    )
  }
  
  // Legacy fallback (dev only)
  if (sortedEarnWays.length === 0 && legacyPoints && legacyPoints.length > 0) {
    if (process.env.NODE_ENV !== 'production') {
      debugLog('[HowToEarnPoints] Using legacy points array. This should be removed once backend is fully deployed.')
    }
    return (
      <Box>
        <ul
          style={{
            paddingLeft: '1.25rem',
            margin: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
          }}
        >
          {legacyPoints.map((text, index) => (
            <li key={index}>
              <Typography variant="subtitle1">{text}</Typography>
            </li>
          ))}
        </ul>
      </Box>
    )
  }
  
  // Render backend-driven earn ways
  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        {sortedEarnWays.map((earnWay) => {
          const isEarned = earnWayStatus[earnWay.key]
          const isInstagramFollow = earnWay.key === 'instagram_follow' || earnWay.key === 'follow-us-on-instagram'
          const isComingSoon = earnWay.key === 'make_reservation'
          
          // Determine badge text
          let badgeText = earnWay.ctaText ?? 'Earn points'
          if (isEarned) {
            if (isInstagramFollow) {
              badgeText = 'Bonus claimed'
            } else {
              badgeText = 'Earned'
            }
          } else if (isComingSoon) {
            badgeText = 'Coming soon'
          }
          
          return (
            <Box
              key={earnWay.key}
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 2,
                opacity: isComingSoon ? 0.6 : 1,
                cursor: isComingSoon ? 'default' : 'auto',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                <Typography variant="subtitle1" sx={{ flex: 1 }}>
                  {earnWay.title}
                </Typography>
                {isComingSoon && (
                  <Chip
                    label="Coming soon"
                    size="small"
                    sx={{
                      backgroundColor: 'transparent',
                      borderColor: 'grey.300',
                      color: 'text.secondary',
                      border: 1,
                      fontWeight: 400,
                      fontSize: '11px',
                      height: '24px',
                    }}
                  />
                )}
              </Box>
              <Chip
                label={badgeText}
                size="small"
                sx={{
                  backgroundColor: isEarned
                    ? isInstagramFollow
                      ? 'grey.100'
                      : 'success.100'
                    : 'transparent',
                  borderColor: isEarned
                    ? isInstagramFollow
                      ? 'grey.300'
                      : 'success.300'
                    : 'grey.300',
                  color: isEarned
                    ? isInstagramFollow
                      ? 'grey.700'
                      : 'success.700'
                    : 'primary.light',
                  border: 1,
                  fontWeight: isEarned ? 500 : 400,
                  fontSize: '12px',
                  height: '28px',
                  opacity: isComingSoon ? 0.6 : 1,
                }}
              />
            </Box>
          )
        })}
      </Box>
      
      {/* Helper text about receipts being optional - only show if upload_receipt earnWay exists */}
      {hasUploadReceipt && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            Receipts are optional — they just help you earn faster.
          </Typography>
        </Box>
      )}
      
      {/* Disclaimer for authenticated users */}
      <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid', borderColor: 'grey.200' }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
          Points unlock rewards. Credits are subject to availability.
        </Typography>
      </Box>
    </Box>
  )
}

export default HowToEarnPoints
